// HM-323 cluster 15.5: filter bar mono-selezione SPEC mockup v3
// §filter-bar. 6 pill (Tutte + 5 stati). Una sola attiva alla volta.
//
// Pill stile (mockup):
//   - inattiva: bg white border 1px #E8DFD1 color #7A6957 font 13/500
//   - hover: bg #FAF7F2 border #D4C7B5
//   - attiva: bg #FCF1EA border 2px #E97B3D color #C4661F font 600
//     (padding compensato 8 15 vs inattiva 9 16 per non shiftare)
//   - dot 10x10 circle a sx (eccetto Tutte)
//   - count badge: bg #F2EBDF font 11/600 color text-primary,
//     attiva: bg #E97B3D color white

import { cn } from '@hotel-manager/ui/lib/cn';
import type { RoomDb } from '../../api/rooms.js';
import { type RoomFilterValue, useRoomFilterStore } from '../../stores/roomFilter.js';
import { mapRoomStatus } from '../dashboard-v2/lib.js';

interface Props {
  rooms: RoomDb[];
}

const PILLS: Array<{ value: RoomFilterValue; label: string }> = [
  { value: 'all', label: 'Tutte' },
  { value: 'free', label: 'Libere' },
  { value: 'reserved', label: 'Riservate' },
  { value: 'occupied', label: 'Occupate' },
  { value: 'dirty', label: 'Da pulire' },
  { value: 'out_of_order', label: 'Guaste' },
];

export function RoomFilterBar({ rooms }: Props) {
  const selected = useRoomFilterStore((s) => s.selectedFilter);
  const setSelected = useRoomFilterStore((s) => s.setSelectedFilter);

  const counts: Record<RoomDb['status'], number> = {
    free: 0,
    reserved: 0,
    occupied: 0,
    dirty: 0,
    out_of_order: 0,
  };
  for (const r of rooms) counts[r.status] += 1;

  const countOf = (v: RoomFilterValue): number => (v === 'all' ? rooms.length : counts[v]);

  return (
    <div className="flex flex-wrap gap-2 items-center mb-5" data-testid="room-filter-bar">
      {PILLS.map((p) => (
        <Pill
          key={p.value}
          value={p.value}
          label={p.label}
          count={countOf(p.value)}
          active={selected === p.value}
          onClick={() => setSelected(p.value)}
        />
      ))}
    </div>
  );
}

function Pill({
  value,
  label,
  count,
  active,
  onClick,
}: {
  value: RoomFilterValue;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  const dotHex = value !== 'all' ? mapRoomStatus(value).borderHex : null;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={`room-filter-pill-${value}`}
      data-active={active}
      className={cn(
        'inline-flex items-center gap-2 rounded-full text-[13px] cursor-pointer',
        'transition-colors duration-150',
        active
          ? 'bg-accent-soft text-accent-primary-text font-semibold border-2 border-accent-primary px-[15px] py-2'
          : 'bg-bg-card text-text-secondary font-medium border border-border-base px-4 py-[9px] hover:bg-bg-base hover:border-border-input',
      )}
    >
      {dotHex && (
        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: dotHex }} />
      )}
      <span>{label}</span>
      <span
        data-testid="pill-count"
        className={cn(
          'inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums',
          active ? 'bg-accent-primary text-text-on-accent' : 'bg-bg-muted text-text-primary',
        )}
      >
        {count}
      </span>
    </button>
  );
}
